<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { fetchItems } from '@/api/replenishments'
import type { IReplenishment } from '@/interfaces/replenishments'
import Pagination from '@/components/Pagination.vue'
import useResize from '@/hooks/useResize'
import moment from 'moment'
import { ElMessage } from 'element-plus'
import apiClient from '@/utils/request'
import MessagePopup, { type DialogData } from './MessagePopup.vue'
import { formatPaymentAmount } from '../useActivePaymentRequests'

type PaymentRequest = IReplenishment & {
  attributes: IReplenishment['attributes'] & { has_crypto_address: boolean }
}
type PaymentInstruction = {
  account: string
  amount: string
  currency: string
  title: string
  message: string
  created_at: string
}

defineProps<{ vatPayer: boolean }>()

const { isDesktop } = useResize()
const items = ref<IReplenishment[]>([])
const total = ref(0)
const page = ref(1)
const limit = 15
const loading = ref(false)
const error = ref(false)
const instructionLoading = ref<string | null>(null)
const dialogData = ref<DialogData>({
  invoice: null,
  message: '',
  account: '',
  amount: null,
  currency: null,
  title: '',
  visible: false,
  createdAt: null
})
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | undefined

function canViewAddress(item: PaymentRequest) {
  return (
    item.attributes.has_crypto_address &&
    item.attributes.status === 'Created' &&
    item.attributes.created_int * 1000 + 86400000 > now.value
  )
}

async function viewAddress(item: PaymentRequest) {
  if (instructionLoading.value) return
  instructionLoading.value = item.id
  try {
    const response = await apiClient.get<{ data: PaymentInstruction }>(
      `/replenishments/${encodeURIComponent(item.id)}/payment_instruction`
    )
    const instruction = response.data.data
    dialogData.value = {
      invoice: null,
      message: instruction.message,
      account: instruction.account,
      amount: instruction.amount,
      currency: instruction.currency,
      title: instruction.title,
      createdAt: instruction.created_at,
      visible: true
    }
  } catch {
    ElMessage.error(
      'Payment instructions could not be loaded. The request may no longer be active.'
    )
    await loadRequests(page.value)
  } finally {
    instructionLoading.value = null
  }
}

function paymentTimeLeft(item: IReplenishment) {
  if (item.attributes.status !== 'Created') return '—'

  const seconds = Math.max(
    0,
    Math.ceil(
      (item.attributes.created_int * 1000 + 86400000 - now.value) / 1000
    )
  )
  if (seconds === 0) return 'Expired'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return [hours, minutes, seconds % 60]
    .map((value) => String(value).padStart(2, '0'))
    .join(':')
}

async function loadRequests(newPage = 1) {
  page.value = newPage
  loading.value = true
  error.value = false
  try {
    const filter = {
      online: false,
      status: ['created', 'rejected', 'voided'],
      created_at_gteq: moment().subtract(14, 'days').format('YYYY-MM-DD')
    }
    const response = await fetchItems({
      start: (newPage - 1) * limit,
      limit,
      filter
    })
    items.value = response.data
    total.value = response.meta.total
  } catch {
    items.value = []
    total.value = 0
    error.value = true
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadRequests()
  timer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})
onUnmounted(() => clearInterval(timer))
</script>

<template>
  <div>
    <MessagePopup
      v-model="dialogData"
      :vat-payer="vatPayer"
      @close="dialogData.visible = false"
    />
    <el-alert v-if="error" type="error" :closable="false" show-icon>
      Could not load payment requests.
      <el-button link type="primary" @click="loadRequests(page)">Retry</el-button>
    </el-alert>
    <el-card v-else>
      <el-table
        v-if="isDesktop"
        v-loading="loading"
        :data="items"
        size="small"
        border
        fit
        highlight-current-row
        style="width: 100%"
      >
        <el-table-column
          align="center"
          label="Date"
          prop="attributes.created_at"
        />
        <el-table-column
          align="center"
          label="Payment system"
          prop="attributes.payment_system_name"
        />
        <el-table-column align="center" label="Amount">
          <template #default="{ row }">
            {{
              formatPaymentAmount(
                row.attributes.amount,
                row.attributes.payment_system_name,
                row.attributes.currency_sign
              )
            }}
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Status"
          prop="attributes.status"
        />
        <el-table-column align="center" label="Time left">
          <template #default="{ row }">
            {{ paymentTimeLeft(row) }}
          </template>
        </el-table-column>
        <el-table-column align="center" label="Address">
          <template #default="{ row }">
            <el-button
              v-if="canViewAddress(row)"
              link
              type="primary"
              class="address-link"
              :loading="instructionLoading === row.id"
              @click="viewAddress(row)"
            >
              <el-icon class="el-icon--left"><ViewElementIcon /></el-icon>
              View address
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-table v-else v-loading="loading" :data="items" style="width: 100%">
        <el-table-column type="expand">
          <template #default="{ row }">
            <p>Date: {{ row.attributes.created_at }}</p>
            <p>Payment method: {{ row.attributes.payment_system_name }}</p>
            <p>
              Amount:
              {{
                formatPaymentAmount(
                  row.attributes.amount,
                  row.attributes.payment_system_name,
                  row.attributes.currency_sign
                )
              }}
            </p>
            <p>Status: {{ row.attributes.status }}</p>
            <p>Time left: {{ paymentTimeLeft(row) }}</p>
            <el-button
              v-if="canViewAddress(row)"
              link
              type="primary"
              class="address-link"
              :loading="instructionLoading === row.id"
              @click="viewAddress(row)"
            >
              <el-icon class="el-icon--left"><ViewElementIcon /></el-icon>
              View address
            </el-button>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Date"
          prop="attributes.created_at"
        />
        <el-table-column align="center" label="Amount">
          <template #default="{ row }">
            {{
              formatPaymentAmount(
                row.attributes.amount,
                row.attributes.payment_system_name,
                row.attributes.currency_sign
              )
            }}
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <Pagination
      v-if="!error && total > limit"
      :total="total"
      :page="page"
      :limit="limit"
      @pagination="loadRequests($event.newPage)"
    />
  </div>
</template>

<style lang="scss" scoped>
.address-link {
  font-size: 12px;
}
</style>
